import { useFormContext } from 'react-hook-form';
import { useEffect } from 'react';
import { EnterpriseBuilderParametersType } from '@mews-commander/data-access';
import { Button, Highlight, IconName, Stack } from '@optimus-web/core';
import { lt, FormInputField } from '@mews/framework';
import { TooltippedIcon } from '@mews-commander/ui';
import { TextKey } from '@mews-commander/enums';
import { EnterpriseBuilderProFormFieldNames, EnterpriseBuilderProFormValues } from '../formMapping';
import { EnterpriseBuilderProFormStep } from '../../../types';

interface ImportToStepProps {
    onSubmit: () => void;
    onStepChange: (step: EnterpriseBuilderProFormStep) => void;
    propertyType: EnterpriseBuilderParametersType;
}

export const ImportToStep = ({ propertyType, onStepChange }: ImportToStepProps) => {
    const {
        reset,
        watch,
        formState: { isValid },
    } = useFormContext<EnterpriseBuilderProFormValues>();
    const enterpriseId = watch(EnterpriseBuilderProFormFieldNames.EnterpriseId);

    useEffect(() => {
        reset({ enterpriseId });
    }, [enterpriseId, propertyType, reset]);

    return (
        <Stack vertical spacing="300">
            {propertyType === EnterpriseBuilderParametersType.NewEnterprise ? (
                <>
                    <Highlight size="medium">{lt('CreateNewProperty')}</Highlight>
                    <FormInputField
                        name={EnterpriseBuilderProFormFieldNames.UniqueName}
                        label={lt('UniqueName')}
                        captionIconWithTooltip={
                            <TooltippedIcon size="small" value={IconName.Info} tooltip={TextKey.UniqueNameHelperText} />
                        }
                        helper={lt('UniqueNameCaption')}
                        required
                        size="medium"
                    />
                    <Highlight size="medium">{lt('CopyPropertyIdentifiers')}</Highlight>
                    <FormInputField
                        name={EnterpriseBuilderProFormFieldNames.SubscriptionId}
                        label={lt('SubscriptionId')}
                        captionIconWithTooltip={
                            <TooltippedIcon
                                size="small"
                                value={IconName.Info}
                                tooltip={TextKey.SubscriptionIdHelperText}
                            />
                        }
                        required
                        size="medium"
                    />
                    <FormInputField
                        name={EnterpriseBuilderProFormFieldNames.ChainId}
                        label={lt('ChainId')}
                        captionIconWithTooltip={
                            <TooltippedIcon size="small" value={IconName.Info} tooltip={TextKey.ChainIdHelperText} />
                        }
                        required
                        size="medium"
                    />
                    <FormInputField
                        name={EnterpriseBuilderProFormFieldNames.CityId}
                        label={lt('CityId')}
                        captionIconWithTooltip={
                            <TooltippedIcon size="small" value={IconName.Info} tooltip={TextKey.CityIdHelperText} />
                        }
                        required
                        size="medium"
                    />
                </>
            ) : (
                <FormInputField
                    name={EnterpriseBuilderProFormFieldNames.PropertyIdentifier}
                    label={lt('PropertyIdentifier')}
                    captionIconWithTooltip={
                        <TooltippedIcon
                            size="small"
                            value={IconName.Info}
                            tooltip={TextKey.PropertyIdentifierHelperText}
                        />
                    }
                    required
                    size="medium"
                />
            )}
            <Stack alignment="end">
                <Button
                    variant="primary"
                    onClick={() => onStepChange(EnterpriseBuilderProFormStep.ExportSettings)}
                    disabled={!isValid}
                    type="submit"
                >
                    {lt('Next')}
                </Button>
            </Stack>
        </Stack>
    );
};
